import { httpClient } from '../client';
import { logger } from '../../../utils/logger';
import { BaseEndpoint } from './base.endpoint';
import { ApiErrorHandler } from '../error.handler';
import { PurchaseAddress } from '../../../types/phone-numbers';

export interface SubaccountDetails {
  twilioSubaccountSid: string;
  friendlyName: string;
  status: string;
  dateCreated: string;
}

export interface CreateSubaccountResponse {
  subaccountSid: string;
  friendlyName: string;
}

class SubaccountEndpoint extends BaseEndpoint {
  private static instance: SubaccountEndpoint;

  private constructor() {
    super('organisations');
  }

  static getInstance(): SubaccountEndpoint {
    if (!SubaccountEndpoint.instance) {
      SubaccountEndpoint.instance = new SubaccountEndpoint();
    }
    return SubaccountEndpoint.instance;
  }

  async getSubaccount(organisationId: string): Promise<SubaccountDetails | null> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<SubaccountDetails>(
        `${this.basePath}/${organisationId}/twilio/subaccount`,
        { headers }
      );
      logger.info('Subaccount fetched successfully');
      return response.data;
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        return null;
      }
      logger.error('Failed to fetch subaccount:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async createSubaccount(
    organisationId: string,
    friendlyName?: string,
    address?: PurchaseAddress
  ): Promise<CreateSubaccountResponse> {
    try {
      const headers = await this.getAuthHeaders();
      const body: Record<string, unknown> = {};
      if (friendlyName) body.friendlyName = friendlyName;
      if (address) body.address = address;
      const response = await httpClient.post<CreateSubaccountResponse>(
        `${this.basePath}/${organisationId}/twilio/subaccount`,
        body,
        { headers }
      );
      logger.info('Subaccount created successfully');
      return response.data;
    } catch (error) {
      logger.error('Failed to create subaccount:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async deleteSubaccount(organisationId: string): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      await httpClient.delete(`${this.basePath}/${organisationId}/twilio/subaccount`, {
        headers,
      });
      logger.info('Subaccount deleted successfully');
    } catch (error) {
      logger.error('Failed to delete subaccount:', error);
      return ApiErrorHandler.handle(error);
    }
  }
}

export const subaccountEndpoint = SubaccountEndpoint.getInstance();
